'use client';

import React from 'react';
import { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { SxProps } from '@mui/system/styleFunctionSx';
import { mergeSx } from '@jucy-ui/appearance';
import { Image } from '@/components/Image';
import { useSiteLinkPrefix } from '@/hooks/useSiteLinkPrefix';
import analytics from '@/services/analytics';
import { prefixCmsUrl } from '@/lib/prefixCmsUrl';
import { CmsLinkImage } from '@/services/cms/models/CmsImageLink';

export interface MarketingCampaignBlockImagesListProps {
    images: CmsLinkImage[];
    sx?: SxProps<Theme>;
}

export const MarketingCampaignBlockImagesList = ({ images, sx }: MarketingCampaignBlockImagesListProps) => {
    const addSiteLinkPrefix = useSiteLinkPrefix();
    return (
        <Stack sx={mergeSx({ gap: 2 }, sx)}>
            {images.map((imageLink) => {
                const hasLink = Boolean(imageLink.link?.url);
                const href = imageLink.link?.url ? addSiteLinkPrefix(imageLink.link.url) : undefined;
                const props = hasLink
                    ? {
                          component: Link,
                          href,
                          target: imageLink.link?.openInNew ? '_blank' : undefined,
                      }
                    : {};
                if (!imageLink.image) {
                    return null;
                }
                return (
                    <Box
                        key={imageLink.id}
                        onClick={() =>
                            analytics.plugins.interactions.trackInteraction({
                                source: 'Marketing campaign block',
                                action: 'click',
                                label: imageLink.image?.title || imageLink.image?.name,
                            })
                        }
                        sx={(theme) => ({
                            display: 'block',
                            ...(hasLink
                                ? {
                                      '&:hover': {
                                          '& img': {
                                              opacity: 0.9,
                                              boxShadow: theme.shadows[4],
                                          },
                                      },
                                  }
                                : undefined),
                        })}
                        {...props}
                    >
                        <Image
                            src={prefixCmsUrl(imageLink.image.link)}
                            alt={imageLink.image.title || imageLink.image.name || ''}
                            width={540}
                            height={308}
                            sx={(theme) => ({
                                height: 'auto',
                                maxWidth: '100%',
                                borderRadius: 1,
                                transition: theme.transitions.create(['opacity', 'box-shadow']),
                                opacity: 1,
                            })}
                        />
                    </Box>
                );
            })}
        </Stack>
    );
};
